/**
 * A composição do pedido — assinatura OU venda avulsa, nunca as duas no mesmo pedido.
 *
 * ## Por que existe
 *
 * Um pedido que juntava item de Assinatura com item avulso gravava o valor recorrente
 * DUAS vezes: a agenda de parcelas nascia do total do pedido (que soma o recorrente) e a
 * assinatura gerava um recebível por ciclo em cima dela. Não era projeção — eram
 * recebíveis que seriam cobrados do cliente. O fundador decidiu em 15/09/2026 que vender
 * os dois no mesmo pedido não é do negócio da agência: são dois pedidos.
 *
 * ## Uma pergunta, um lugar
 *
 * A tela (para avisar na hora e não montar a agenda) e a API (para recusar em todas as
 * portas de escrita) fazem a MESMA pergunta, e as duas a fazem aqui. A mesma regra
 * escrita duas vezes divergiria na primeira forma de venda nova do catálogo.
 *
 * ## A recorrência vem do COMPORTAMENTO da forma resolvida
 *
 * A forma resolvida é `COALESCE(subproduto.forma_venda_id, produto.forma_venda_id)` (DS-54),
 * e o que decide é o COMPORTAMENTO dela (`forma_venda.comportamento`, D-146), nunca o nome,
 * que é livre e só de exibição, e nunca `tipo_produto.recorrente`, que é do produto inteiro
 * e marcaria também o PageSignal · Setup. Quem resolve a forma é o chamador: a tela lê o
 * catálogo, a API lê o banco. Aqui entra o comportamento da forma já resolvida.
 *
 * ## O terceiro estado
 *
 * `forma_venda_id` é anulável nos dois níveis, e o PageSignal nasce assim de propósito: é o
 * SUBPRODUTO (Setup ou Assinatura) que diz o que foi vendido. Item sem forma resolvida é
 * INDETERMINADO — nem recorrente nem avulso. Tratá-lo como avulso seria decidir no lugar
 * de quem vendeu: um PageSignal sem subproduto ganharia agenda de parcelas e, se fosse
 * assinatura, voltaria a cobrar duas vezes. Onde a natureza decide dinheiro (agenda ou
 * assinatura), o indeterminado é recusado com o que falta dizer.
 */

/**
 * Os comportamentos que o sistema sabe tratar (D-146, DS-553): os valores do CHECK de
 * `forma_venda.comportamento`. O nome da forma é livre; só um comportamento novo pede código.
 */
export const COMPORTAMENTOS_DE_VENDA = ['com_veiculacao', 'sem_veiculacao', 'assinatura'] as const
export type ComportamentoDeVenda = (typeof COMPORTAMENTOS_DE_VENDA)[number]

export type NaturezaDoItem = 'recorrente' | 'avulsa' | 'indeterminada'

/**
 * A natureza de UM item, a partir do comportamento da forma de venda já resolvida.
 *
 * Com veiculação e sem veiculação são vendas avulsas (geram agenda de parcelas própria);
 * assinatura é recorrente. Nulo, ausente ou valor desconhecido é indeterminado.
 */
export function naturezaDoItem(comportamento: string | null | undefined): NaturezaDoItem {
  if (comportamento === 'assinatura') return 'recorrente'
  return comportamento === 'com_veiculacao' || comportamento === 'sem_veiculacao' ? 'avulsa' : 'indeterminada'
}

/** O pedido pede as datas de início e término da campanha só no comportamento Com veiculação. */
export function pedeDatasDeVeiculacao(comportamento: string | null | undefined): boolean {
  return comportamento === 'com_veiculacao'
}

export type TipoDaComposicao = 'vazia' | 'assinatura' | 'avulsa' | 'mista' | 'indeterminada'

export interface ComposicaoDoPedido {
  readonly tipo: TipoDaComposicao
  /** Há ao menos um item recorrente: é o que liga o regime de assinatura. */
  readonly temRecorrente: boolean
  /** Há ao menos um item avulso (comportamento com ou sem veiculação). */
  readonly temAvulsa: boolean
  /** Posições, na ordem recebida, dos itens sem comportamento resolvido. */
  readonly indeterminados: readonly number[]
}

/**
 * A composição de um conjunto de itens.
 *
 * `mista` vence `indeterminada`: recorrente e avulso juntos já são a mistura, qualquer
 * que seja o terceiro item.
 */
export function composicaoDoPedido(comportamentos: readonly (string | null | undefined)[]): ComposicaoDoPedido {
  const naturezas = comportamentos.map(naturezaDoItem)
  const temRecorrente = naturezas.includes('recorrente')
  const temAvulsa = naturezas.includes('avulsa')
  const indeterminados = naturezas.flatMap((natureza, i) => (natureza === 'indeterminada' ? [i] : []))
  let tipo: TipoDaComposicao = 'avulsa'
  if (naturezas.length === 0) tipo = 'vazia'
  else if (temRecorrente && temAvulsa) tipo = 'mista'
  else if (indeterminados.length > 0) tipo = 'indeterminada'
  else if (temRecorrente) tipo = 'assinatura'
  return { tipo, temRecorrente, temAvulsa, indeterminados }
}

/**
 * O regime financeiro que o pedido TERÁ depois da escrita que se está conferindo.
 *
 * Quem chama responde pelos dois fatos: o que já está gravado mais o que a própria
 * escrita acrescenta — uma agenda que chega no mesmo `POST` conta como agenda.
 */
export interface RegimeDoPedido {
  /** Existe assinatura presa ao pedido, em qualquer status: os recebíveis nascem do ciclo. */
  readonly temAssinatura: boolean
  /** Existe (ou vai existir) parcela da agenda comum: fora de ciclo e não cancelada. */
  readonly temAgendaComum: boolean
}

export type RecusaDeComposicao =
  | 'composicao_mista'
  | 'avulso_em_pedido_de_assinatura'
  | 'item_sem_forma_de_venda'
  | 'assinatura_com_agenda'

/**
 * A invariante do pedido, conferida em toda porta que escreve itens, agenda ou assinatura.
 *
 * Um pedido é de UM regime: ou recorrente (itens de comportamento assinatura, recebíveis por
 * ciclo), ou avulso (itens com ou sem veiculação, agenda de parcelas). A ordem das perguntas é a da
 * mensagem mais útil: a mistura explícita primeiro, depois o pedido de assinatura que
 * ganhou item avulso, depois o item que ainda não disse o que é, e por fim a assinatura
 * que encontraria uma agenda.
 */
export function recusaDeComposicao(
  composicao: ComposicaoDoPedido,
  regime: RegimeDoPedido,
): RecusaDeComposicao | null {
  if (composicao.tipo === 'mista') return 'composicao_mista'
  if (regime.temAssinatura && composicao.temAvulsa) return 'avulso_em_pedido_de_assinatura'
  const regimeDeAssinatura = composicao.temRecorrente || regime.temAssinatura
  if (composicao.indeterminados.length > 0 && (regimeDeAssinatura || regime.temAgendaComum)) {
    return 'item_sem_forma_de_venda'
  }
  if (regimeDeAssinatura && regime.temAgendaComum) return 'assinatura_com_agenda'
  return null
}

/** O item que falta classificar, para a mensagem dizer QUAL e o que fazer com ele. */
export interface ItemSemFormaDeVenda {
  readonly produto: string
  /** O produto tem subprodutos: a pessoa resolve escolhendo um deles. */
  readonly temSubprodutos: boolean
}

/**
 * O texto de produto de cada recusa — o mesmo na tela e na resposta da API.
 *
 * Diz o que fazer, e nada do vocabulário de implementação: nem "forma de venda
 * incompatível", nem "natureza", nem os termos proibidos pela D-72.
 */
export function mensagemDaRecusaDeComposicao(
  recusa: RecusaDeComposicao,
  itemSemForma?: ItemSemFormaDeVenda,
): string {
  switch (recusa) {
    case 'composicao_mista':
      return 'Produtos recorrentes e vendas avulsas devem ficar em pedidos separados. Crie um pedido para a assinatura e outro para os demais itens.'
    case 'avulso_em_pedido_de_assinatura':
      return 'Este pedido é de assinatura, e produtos recorrentes e vendas avulsas devem ficar em pedidos separados. Crie outro pedido para os demais itens.'
    case 'assinatura_com_agenda':
      return 'A assinatura gera os próprios recebíveis a cada ciclo e não divide o pedido com parcelas de venda avulsa. Crie um pedido separado para a assinatura.'
    case 'item_sem_forma_de_venda':
      if (itemSemForma === undefined) {
        return 'Escolha o sub-produto de cada item: é ele que diz se o item é assinatura ou venda avulsa.'
      }
      return itemSemForma.temSubprodutos
        ? `Escolha o sub-produto de ${itemSemForma.produto}: é ele que diz se o item é assinatura ou venda avulsa.`
        : `${itemSemForma.produto} está sem forma de venda no catálogo. Complete o cadastro do produto antes de vendê-lo.`
  }
}

/**
 * A mesma regra na conversão Negócio → Pedido, com o caminho de saída que existe lá.
 *
 * O negócio pode juntar mensalidade e itens pontuais (é pipeline, não cobrança); o pedido
 * que nasce dele não. Como um negócio gera UM pedido, o que a pessoa faz é deixar no
 * negócio os itens de um tipo e cadastrar o outro pedido direto.
 */
export const MENSAGEM_NEGOCIO_MISTO =
  'Produtos recorrentes e vendas avulsas devem ficar em pedidos separados. Deixe no negócio só os itens de um tipo antes de gerar o pedido e crie o outro pedido pelo cadastro direto.'
